// The routing rules people taught Merlin from the quality door (kanban
// 143454d3) — Scott's curation beside the Quality Routing table: what each
// rule says, who taught it from which item, whether Merlin's Report Memory
// took it, and Retire (never delete) when a rule should stop applying.
// Backend: GET/POST /api/admin/signals/routing-instructions.

import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';

import { trackEventNow } from '../services/portalTracking';
import {
  instructionRuleLine,
  memoryLabel,
  routingInstructionsApi,
  type RoutingInstruction,
  type RoutingInstructionStatus,
} from '../services/qualityRouting';
import styles from './QualityRoutingAdminPage.module.css';

function errorText(err: unknown): string {
  if (err instanceof AxiosError) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}

function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface RowProps {
  row: RoutingInstruction;
  onRetired: (row: RoutingInstruction) => void;
}

function InstructionRow({ row, onRetired }: RowProps): React.ReactElement {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retire = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await routingInstructionsApi.retire(row.id, reason.trim() || undefined);
      trackEventNow('action', 'admin_routing_instruction_retire', {
        instruction_id: row.short_id,
        thread_no: row.thread_no,
        with_reason: reason.trim().length > 0,
      });
      onRetired(updated);
      setConfirming(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className={styles.row} data-testid="routing-instruction" data-status={row.status}>
      <td>
        <span className={styles.threadNo}>{row.short_id}</span>{' '}
        <span className={styles.title}>{instructionRuleLine(row)}</span>
        <div className={styles.reason}>{row.reason}</div>
      </td>
      <td>
        {row.recorded_by_name}
        <div className={styles.age}>
          from {row.thread_no} · {shortDate(row.created_at)}
        </div>
      </td>
      <td>
        <span className={styles.status} data-status={row.status === 'active' ? 'routed' : 'dismissed'}>
          {row.status === 'active' ? 'Active' : 'Retired'}
        </span>
        {row.status === 'retired' && (
          <div className={styles.age}>
            {shortDate(row.retired_at)}
            {row.retired_reason ? ` · ${row.retired_reason}` : ''}
          </div>
        )}
      </td>
      <td className={styles.age} data-testid="memory-state">
        {memoryLabel(row)}
      </td>
      <td>
        {row.status === 'active' && !confirming && (
          <button
            type="button"
            className={styles.inlineLink}
            onClick={() => setConfirming(true)}
            disabled={busy}
          >
            Retire
          </button>
        )}
        {row.status === 'active' && confirming && (
          <div className={styles.retireBox}>
            <input
              className={styles.search}
              placeholder="Why it should stop (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-label={`Why retire ${row.short_id}`}
              disabled={busy}
            />
            <button type="button" className={styles.inlineLink} onClick={() => void retire()} disabled={busy}>
              Retire {row.short_id}
            </button>
            <button
              type="button"
              className={styles.inlineLink}
              onClick={() => {
                setConfirming(false);
                setReason('');
              }}
              disabled={busy}
            >
              Keep
            </button>
            {error && (
              <span className={styles.loadError} role="alert">
                {error}
              </span>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

export function QualityRoutingInstructions(): React.ReactElement {
  const [rows, setRows] = useState<RoutingInstruction[]>([]);
  const [counts, setCounts] = useState<Partial<Record<RoutingInstructionStatus, number>>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await routingInstructionsApi.list();
      setRows(res.items);
      setCounts(res.counts);
    } catch (err) {
      setLoadError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = showRetired ? rows : rows.filter((r) => r.status === 'active');
  const active = counts.active ?? 0;
  const retired = counts.retired ?? 0;

  return (
    <section className={styles.rulesSection} aria-label="Rules people taught Merlin" data-testid="routing-instructions">
      <div className={styles.rulesHead}>
        <h2>Rules people taught Merlin</h2>
        <p>
          From the quality door’s “Make this a rule” / “Don’t send me items like this”. Merlin
          applies an active rule on the next similar item and quotes it on the item; retiring
          tells Merlin to stop. Nothing is deleted.
        </p>
        <div className={styles.rulesCounts}>
          <span>{active} active</span>
          {retired > 0 && (
            <button
              type="button"
              className={styles.inlineLink}
              onClick={() => setShowRetired((v) => !v)}
              aria-pressed={showRetired}
            >
              {showRetired ? 'Hide' : 'Show'} {retired} retired
            </button>
          )}
        </div>
      </div>
      {loading && <p className={styles.muted}>Loading rules…</p>}
      {loadError && (
        <div className={styles.loadError}>
          {loadError}{' '}
          <button type="button" className={styles.inlineLink} onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}
      {!loading && !loadError && visible.length === 0 && (
        <p className={styles.muted}>No rules taught yet.</p>
      )}
      {!loading && !loadError && visible.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Rule</th>
                <th>Taught by</th>
                <th>Status</th>
                <th>Merlin</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <InstructionRow
                  key={row.id}
                  row={row}
                  onRetired={(updated) => {
                    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
                    setCounts((prev) => ({
                      ...prev,
                      active: Math.max(0, (prev.active ?? 1) - 1),
                      retired: (prev.retired ?? 0) + 1,
                    }));
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default QualityRoutingInstructions;
